import { useEffect, useState } from 'react';
import { Table, TableRow, TableCell, TableBody, Icon } from '@bosch/react-frok';

import { useSpace } from '@/hooks/useSpace';
import { useNavigation } from '@/hooks/useNavigation';
import AssistantIcon from '@/ui/components/AssistantIcon/AssistantIcon';
import { ChatMessage } from '@/shared/types/chat';
import { formatTimestamp, getTimestampRefreshDelay } from '@/utils/chatUtils';
import type { ChatHistoryProps } from '@/shared/types/chat';
import type { ChatHistoryTimestampProps } from './types/ChatHistoryTypes';

import './ChatHistory.scss';

const ChatHistoryTimestamp = ({ timestamp, translate }: ChatHistoryTimestampProps) => {
    const [value, setValue] = useState(() => formatTimestamp(timestamp, translate))
    const refreshDelay = getTimestampRefreshDelay(timestamp)

    useEffect(() => {
        if (!timestamp || refreshDelay <= 0) return

        const intervalId = setInterval(() => {
            setValue(formatTimestamp(timestamp, translate))
        }, refreshDelay)

        return () => clearInterval(intervalId)
    }, [timestamp, refreshDelay, translate])

    return <>{value}</>
}

const ChatHistory: React.FC<ChatHistoryProps> = ({ chatMessages, translate, isMobile }) => {
    const { switchAssistant } = useSpace()
    const { navigateTo } = useNavigation()

    const buildChatPath = (path: string, chatId?: string | number) => {
        if (!chatId) return path;

        const searchParams = new URLSearchParams();
        searchParams.set('chatId', String(chatId));

        return `${path}?${searchParams.toString()}`;
    }

    const editChat = async (chat: ChatMessage) => {
        const { assistantId, path, chatId } = chat;

        if (path) {
            navigateTo(buildChatPath(path, chatId));
            if (assistantId) await switchAssistant(assistantId, chatId)
        }
    }

    return (
        <div className="chat-history">
            <div className='title-last-chats-wrapper'>
                <strong className='title'>{translate('home:lastChats')}</strong>
            </div>
            <Table>
                <TableBody>
                    {chatMessages.map((chat: ChatMessage) => (
                        <TableRow key={chat.chatId} onClick={() => editChat(chat)} className='chat-history-row'>
                            {isMobile ? (
                                <TableCell className='mobile-chat-cell'>
                                    <div className='mobile-chat-label-group'>
                                        <AssistantIcon icon={chat.icon} iconSrc={chat.iconSrc} alt={chat.label} />
                                        <div className='mobile-chat-label'>
                                            <span className='mobile-chat-label-text'>{chat.label}</span>
                                            <span className='mobile-chat-timestamp'><ChatHistoryTimestamp timestamp={chat.timestamp} translate={translate} /></span>
                                        </div>
                                    </div>
                                    <div className='mobile-chat-actions'>
                                        <Icon className='edit-icon' iconName="edit" />
                                    </div>
                                </TableCell>
                            ) : (
                                <>
                                    <TableCell className='desktop-cell desktop-label-cell'>
                                        <div className='chat-label-group'>
                                            <AssistantIcon icon={chat.icon} iconSrc={chat.iconSrc} alt={chat.label} />
                                            <span className='chat-label-text'>{chat.label}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className='desktop-cell desktop-timestamp-cell'><ChatHistoryTimestamp timestamp={chat.timestamp} translate={translate} /></TableCell>
                                    <TableCell className='desktop-cell desktop-actions-cell'>
                                        <Icon className='edit-icon' iconName="edit" />
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default ChatHistory;